"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { images } from "../../constants";
import { AppWrap, MotionWrap } from "../../wrapper";
import "./Footer.scss";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    message: "",
  });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { username, email, message } = formData;

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!username || !email || !message) {
      alert("All fields are required!");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("/.netlify/functions/sendEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          email,
          message,
          title: "BBM Cybersecurity Contact Form",
          subject: "New Contact Form Submission",
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setIsFormSubmitted(true);
      } else {
        console.error("Email failed:", data?.error || "unknown");
      }
    } catch (error) {
      console.error("An error occurred:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.h2
        className="head-text footer__title"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        Take a coffee & chat with us
      </motion.h2>

      <motion.div
        className="footer__grid"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        {/* Left column: contact cards */}
        <div className="footer__left">
          <div className="footer__card app__footer-card">
            <img src={images.email} alt="email icon" className="footer__icon" />
            <div>
              <div className="p-text footer__card-title">Email</div>
              <a
                className="p-text footer__link"
                href="mailto:info@bbmcyberspace.com"
              >
                info@bbmcyberspace.com
              </a>
            </div>
          </div>

          <div className="footer__addresses">
            <div className="footer__address-card app__footer-card">
              <div className="footer__address-head">
                <svg
                  aria-hidden
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="footer__loc-icon"
                >
                  <path
                    fill="currentColor"
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"
                  />
                </svg>
                <div className="p-text">
                  <strong>Nigeria Office</strong>
                </div>
              </div>
              <div className="p-text footer__address capitalize">
                33 Mohamadu Buhari Street, Peace Court Estate, Lokogoma,
                <br />
                Abuja, FCT, Nigeria
              </div>
            </div>

            <div className="footer__address-card app__footer-card">
              <div className="footer__address-head">
                <svg
                  aria-hidden
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  className="footer__loc-icon"
                >
                  <path
                    fill="currentColor"
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"
                  />
                </svg>
                <div className="p-text">
                  <strong>UK Office</strong>
                </div>
              </div>
              <div className="p-text footer__address">
                212 Clara Street, Newcastle upon Tyne,
                <br />
                United Kingdom, NE4 8PY
              </div>
            </div>
          </div>
        </div>

        {/* Right column: contact form */}
        <div className="footer__right app__primarybg !rounded-xl !p-4 !text-white">
          {!isFormSubmitted ? (
            <form
              className="footer__form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              aria-label="Contact form"
            >
              <label className="sr-only" htmlFor="username">
                Your name
              </label>
              <input
                id="username"
                name="username"
                className="p-text footer__input"
                type="text"
                placeholder="Your name"
                value={username}
                onChange={handleChangeInput}
                required
              />

              <label className="sr-only" htmlFor="email">
                Your email
              </label>
              <input
                id="email"
                name="email"
                className="p-text footer__input"
                type="email"
                placeholder="Your email"
                value={email}
                onChange={handleChangeInput}
                required
              />

              <label className="sr-only" htmlFor="message">
                Your message
              </label>
              <textarea
                id="message"
                name="message"
                className="p-text footer__textarea"
                placeholder="Your message"
                value={message}
                onChange={handleChangeInput}
                rows={5}
                required
              />

              <div className="footer__form-actions">
                <button
                  type="submit"
                  className="btn footer__btn"
                  aria-disabled={loading}
                >
                  {!loading ? "Send Message" : "Sending..."}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost footer__btn-cancel"
                  onClick={() =>
                    setFormData({ username: "", email: "", message: "" })
                  }
                >
                  Reset
                </button>
              </div>
            </form>
          ) : (
            <motion.div
              className="footer__thanks"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
            >
              <h3 className="head-text">Thank you — we’ll be in touch!</h3>
              <p className="p-text">
                We typically respond within 1–2 business days.
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* bottom row: copyright */}
      <div className="footer__bottom">
        <div className="footer__bottom-inner p-text">
          <span>
            2018 - {currentYear} <strong>BBM.</strong> All rights reserved.
          </span>
        </div>
      </div>
    </>
  );
};

export default AppWrap(
  MotionWrap(Footer, "app__footer"),
  "contact",
  "app__whitebg"
);
